'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import BrandLogo from '@/components/brand-logo';
import { useI18n } from '@/components/i18n-provider';
import { useContent } from '@/hooks/use-content';

const navLinkClass =
  'text-xs font-semibold tracking-[0.14em] uppercase text-white/85 hover:text-white transition-colors whitespace-nowrap';

export default function Header({ variant = 'dark' }: { variant?: 'dark' | 'light' }) {
  const [open, setOpen] = useState(false);
  const { locale, setLocale } = useI18n();
  const c = useContent();
  const isDark = variant === 'dark';

  const shellClass = isDark
    ? 'bg-[var(--bm-black)]/95 backdrop-blur-md border-b border-white/8 text-white'
    : 'bg-[var(--bm-white)]/92 backdrop-blur-md border-b border-[var(--bm-light)] text-[var(--bm-charcoal)]';

  const mobileBtnClass = isDark ? 'text-white' : 'text-[var(--bm-charcoal)]';
  const mobileNavClass = isDark
    ? 'border-white/10 bg-[var(--bm-black)]'
    : 'border-[var(--bm-light)] bg-[var(--bm-white)]';

  const linkClass = isDark
    ? navLinkClass
    : navLinkClass.replace('text-white/85', 'text-[var(--bm-mid)]').replace('hover:text-white', 'hover:text-[var(--bm-charcoal)]');

  const navLinks = [
    c.nav.home,
    c.nav.about,
    c.nav.services,
    c.nav.articles,
    c.nav.reviews,
  ];

  const langToggleClass = isDark
    ? 'text-[10px] font-bold tracking-wider uppercase border border-white/25 px-2 py-1 rounded-sm hover:bg-white/10 transition-colors'
    : 'text-[10px] font-bold tracking-wider uppercase border border-[var(--bm-light)] px-2 py-1 rounded-sm hover:bg-[var(--bm-pale)] transition-colors';

  return (
    <header className={`fixed top-0 w-full z-50 ${shellClass}`}>
      <div className="bm-container">
        <div className="flex items-center justify-between gap-4 h-[80px] md:h-[92px]">
          <div className="flex items-center gap-3 shrink-0">
            <BrandLogo variant={isDark ? 'dark' : 'light'} size="md" />
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 px-6 min-w-0">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href} className={linkClass}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className={`hidden sm:flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-[var(--bm-mid)]'}`}>
              <button
                type="button"
                onClick={() => setLocale('ar')}
                className={`${langToggleClass} ${locale === 'ar' ? (isDark ? 'bg-white/15 text-white' : 'bg-[var(--bm-pale)] text-[var(--bm-charcoal)]') : ''}`}
                aria-pressed={locale === 'ar'}
              >
                AR
              </button>
              <button
                type="button"
                onClick={() => setLocale('en')}
                className={`${langToggleClass} ${locale === 'en' ? (isDark ? 'bg-white/15 text-white' : 'bg-[var(--bm-pale)] text-[var(--bm-charcoal)]') : ''}`}
                aria-pressed={locale === 'en'}
              >
                EN
              </button>
            </div>

            <Link
              href={c.nav.cta.href}
              className={`hidden lg:inline-flex ${isDark ? 'bm-btn-light !px-4 !py-2.5 !text-[10px]' : 'bm-btn !px-4 !py-2.5 !text-[10px]'}`}
            >
              {c.nav.cta.label}
            </Link>

            <button
              type="button"
              className={`lg:hidden p-2 ${mobileBtnClass}`}
              aria-label={locale === 'ar' ? 'القائمة' : 'Menu'}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open ? (
          <nav className={`lg:hidden border-t py-4 flex flex-col gap-4 ${mobileNavClass}`}>
            <div className="flex items-center gap-2 px-2 sm:hidden">
              <button
                type="button"
                onClick={() => setLocale('ar')}
                className={`${langToggleClass} ${locale === 'ar' ? 'bg-[var(--bm-pale)]' : ''}`}
              >
                AR
              </button>
              <button
                type="button"
                onClick={() => setLocale('en')}
                className={`${langToggleClass} ${locale === 'en' ? 'bg-[var(--bm-pale)]' : ''}`}
              >
                EN
              </button>
            </div>
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href} className="text-sm font-semibold px-2" onClick={() => setOpen(false)}>
                {link.label}
              </Link>
            ))}
            <Link href={c.nav.cta.href} className="bm-btn mx-2 text-center" onClick={() => setOpen(false)}>
              {c.nav.cta.label}
            </Link>
          </nav>
        ) : null}
      </div>
    </header>
  );
}
